import { Route, RouteConfig } from 'vue-router';
import constants, { locales } from '@adobe/edex/frontend/mixins/constants';
import {
  CourseTypeRoutePath,
  ProfessionalLearningRedirectRoute,
  RedirectRoutePath,
} from '@adobe/edex/ui/shared/constants';
import { ExternalRoutePath } from '.';

const withLocale = (path: string) => `/:${constants.localeParam}(${locales.join('|')})?${path}`;

const TEACHING_DASHBOARD = 'teaching-dashboard';
const spaReloadRoute = ({ path, name, ...rest }: Partial<RouteConfig>): RouteConfig => ({
  ...rest,
  path: withLocale(path),
  name,
  beforeEnter: (to: Route) => {
    if (to.fullPath.includes('/browse')) {
      window.location.href = to.fullPath.replace('/browse', '/search');
    } else {
      window.location.href = to.fullPath;
    }
  },
});

const COURSE_TYPE_ROUTE_PATH_VALUES = Object.values(CourseTypeRoutePath).join('|');

const getProfessionalLearningRedirects = (): RouteConfig[] => {
  const routeConfigs: RouteConfig[] = [];
  ProfessionalLearningRedirectRoute.forEach((value, key) => {
    routeConfigs.push({
      path: withLocale(`/professional-learning/self-paced-course${key}`),
      redirect: (to) => {
        return to.fullPath.replace(key, value);
      },
    });
  });

  routeConfigs.push({
    path: withLocale('/professional-learning/toolkit/adobe-spark-professional-learning-kit'),
    redirect: (to) => {
      return to.fullPath.replace(
        'adobe-spark-professional-learning-kit',
        'adobe-creative-cloud-express-professional-learning-kit'
      );
    },
  });

  return routeConfigs;
};

export const routes: RouteConfig[] = [
  {
    path: withLocale(`/pd/course/*`),
    redirect: (to) => {
      return to.fullPath.replace('/pd/course/', '/professional-learning/self-paced-course/');
    },
  },
  {
    path: withLocale(`/member/*`),
    redirect: (to) => {
      return to.fullPath.replace('/member/', '/community/member/');
    },
  },
  {
    path: withLocale(`/discussion/*`),
    redirect: (to) => {
      return to.fullPath.replace('/discussion/', '/community/discussion/');
    },
  },
  {
    path: withLocale(`/new-discussion`),
    redirect: (to) => {
      return '/community/discussion/new';
    },
  },

  {
    path: withLocale('/max'),
    beforeEnter: () => {
      window.location.replace(ExternalRoutePath.max);
    },
    meta: {
      public: true,
    },
  },

  ...getProfessionalLearningRedirects(),

  {
    path: withLocale(`/professional-learning/:coursetype(${COURSE_TYPE_ROUTE_PATH_VALUES})/:vanityurl`),
    component: () =>
      import(
        /* webpackChunkName: "course" */ /* webpackPrefetch: true */ /* webpackPreload: true */ /* webpackMode: "lazy" */ '../views/course/course.vue'
      ),
    meta: {
      public: true,
    },
    children: [
      {
        path: '',
        component: () =>
          import(
            /* webpackChunkName: "course" */ /* webpackPrefetch: true */ /* webpackPreload: true */ /* webpackMode: "lazy" */ '../views/course/course-about.vue'
          ),
        name: 'course-about',
        meta: {
          public: true,
        },
      },
      {
        path: 'instructors',
        component: () =>
          import(
            /* webpackChunkName: "course" */ /* webpackPrefetch: true */ /* webpackPreload: true */ /* webpackMode: "lazy" */ '../views/course/course-instructors.vue'
          ),
        name: 'course-instructors',
        meta: {
          public: true,
        },
      },
      {
        path: 'related',
        component: () =>
          import(
            /* webpackChunkName: "course" */ /* webpackPrefetch: true */ /* webpackPreload: true */ /* webpackMode: "lazy" */ '../views/course/course-related.vue'
          ),
        name: 'course-related',
        meta: {
          public: true,
        },
      },
      {
        path: 'enroll',
        component: () =>
          import(
            /* webpackChunkName: "course" */ /* webpackPrefetch: true */ /* webpackPreload: true */ /* webpackMode: "lazy" */ '../views/course/course-about.vue'
          ),
        name: 'course-enroll',
      },
      {
        path: 'announcements',
        component: () =>
          import(
            /* webpackChunkName: "course" */ /* webpackPrefetch: true */ /* webpackPreload: true */ /* webpackMode: "lazy" */ '../views/course/course-announcements.vue'
          ),
        name: 'course-announcements',
      },
      {
        path: 'overview',
        component: () =>
          import(
            /* webpackChunkName: "course" */ /* webpackPrefetch: true */ /* webpackPreload: true */ /* webpackMode: "lazy" */ '../views/course/course-overview.vue'
          ),
        name: 'course-overview',
      },
      {
        path: 'manage',
        component: () =>
          import(
            /* webpackChunkName: "course-manage" */ /* webpackPrefetch: true */ /* webpackPreload: true */ /* webpackMode: "lazy" */ '../views/course/course-manage.vue'
          ),
        name: 'course-manage',
      },
      {
        path: 'settings',
        component: () =>
          import(
            /* webpackChunkName: "course-manage" */ /* webpackPrefetch: true */ /* webpackPreload: true */ /* webpackMode: "lazy" */ '../views/course/course-settings.vue'
          ),
        name: 'course-settings',
        pathToRegexpOptions: { strict: true },
      },
      {
        path: 'templates',
        component: () =>
          import(
            /* webpackChunkName: "course-manage" */ /* webpackPrefetch: true */ /* webpackPreload: true */ /* webpackMode: "lazy" */ '../views/course/course-notification-templates.vue'
          ),
        name: 'course-templates',
      },
      {
        path: 'workshop-builder',
        component: () =>
          import(
            /* webpackChunkName: "course" */ /* webpackPrefetch: true */ /* webpackPreload: true */ /* webpackMode: "lazy" */ '../views/course/course-workshop-builder.vue'
          ),
        name: 'course-workshop-builder',
      },
      {
        path: 'e',
        component: () =>
          import(
            /* webpackChunkName: "course" */ /* webpackPrefetch: true */ /* webpackPreload: true */ /* webpackMode: "lazy" */ '../views/course/explorer/course-explorer-modal.vue'
          ),
        name: 'course-explorer',
        children: [
          {
            path: 'forum/:discussionID?',
            component: () =>
              import(
                /* webpackChunkName: "course" */ /* webpackPrefetch: true */ /* webpackPreload: true */ /* webpackMode: "lazy" */ '../views/course/explorer/course-program.vue'
              ),
            name: 'course-explorer-program-discussion',
            meta: {
              activeTab: 'forums',
            },
          },
          {
            path: 'new-discussion',
            component: () =>
              import(
                /* webpackChunkName: "course" */ /* webpackPrefetch: true */ /* webpackPreload: true */ /* webpackMode: "lazy" */ '../views/course/explorer/course-program.vue'
              ),
            name: 'course-explorer-new-discussion',
            meta: {
              activeTab: 'forums',
              newDiscussion: true,
            },
          },
          {
            path: 'workshop/:workshopVanityURL/step/:progressIndex',
            component: () =>
              import(
                /* webpackChunkName: "course" */ /* webpackPrefetch: true */ /* webpackPreload: true */ /* webpackMode: "lazy" */ '../views/course/explorer/course-program.vue'
              ),
            name: 'course-explorer-program-step',
          },
          {
            path: 'workshop/:workshopVanityURL/step/:progressIndex/assignment/:assignmentID',
            component: () =>
              import(
                /* webpackChunkName: "course" */ /* webpackPrefetch: true */ /* webpackPreload: true */ /* webpackMode: "lazy" */ '../views/course/explorer/course-program.vue'
              ),
            name: 'course-explorer-program-assignment',
          },
          {
            path: 'page/:staticVanityURL',
            component: () =>
              import(
                /* webpackChunkName: "course" */ /* webpackPrefetch: true */ /* webpackPreload: true */ /* webpackMode: "lazy" */ '../views/course/explorer/course-static-page.vue'
              ),
            name: 'course-explorer-help',
          },
          {
            path: 'program/:staticVanityURL',
            component: () =>
              import(
                /* webpackChunkName: "course" */ /* webpackPrefetch: true */ /* webpackPreload: true */ /* webpackMode: "lazy" */ '../views/course/explorer/course-static-page.vue'
              ),
            name: 'course-explorer-program-static',
          },
          // Default / Not Found
          {
            path: '*',
            component: () =>
              import(
                /* webpackChunkName: "home" */ /* webpackPrefetch: true */ /* webpackPreload: true */ /* webpackMode: "lazy" */ '../views/not-found.vue'
              ),
            name: 'course-explorer-not-found',
            meta: {
              public: true,
            },
          },
        ],
      },
      {
        path: '*',
        component: () =>
          import(
            /* webpackChunkName: "course" */ /* webpackPrefetch: true */ /* webpackPreload: true */ /* webpackMode: "lazy" */ '../views/course/course-about.vue'
          ),
        name: 'default-to-course-about',
      },
    ],
  },

  {
    path: withLocale(`/edu-student-course/:vanityurl`),
    component: () =>
      import(
        /* webpackChunkName: "course" */ /* webpackPrefetch: true */ /* webpackPreload: true */ /* webpackMode: "lazy" */ '../views/externalCourse/externalCourse.vue'
      ),
    meta: {
      public: true,
    },
    children: [
      {
        path: '',
        component: () =>
          import(
            /* webpackChunkName: "course" */ /* webpackPrefetch: true */ /* webpackPreload: true */ /* webpackMode: "lazy" */ '../views/externalCourse/externalCourse-about.vue'
          ),
        name: 'externalCourse-about',
        meta: {
          public: true,
        },
      },
      {
        path: 'about',
        component: () =>
          import(
            /* webpackChunkName: "course" */ /* webpackPrefetch: true */ /* webpackPreload: true */ /* webpackMode: "lazy" */ '../views/externalCourse/externalCourse-about.vue'
          ),
        name: 'externalCourse-about',
        meta: {
          public: true,
        },
      },
      {
        path: 'manage',
        component: () =>
          import(
            /* webpackChunkName: "course-manage" */ /* webpackPrefetch: true */ /* webpackPreload: true */ /* webpackMode: "lazy" */ '../views/externalCourse/externalCourse-manage.vue'
          ),
        name: 'externalCourse-manage',
      },
      {
        path: 'settings',
        component: () =>
          import(
            /* webpackChunkName: "course-manage" */ /* webpackPrefetch: true */ /* webpackPreload: true */ /* webpackMode: "lazy" */ '../views/externalCourse/externalCourse-settings.vue'
          ),
        name: 'externalCourse-settings',
        pathToRegexpOptions: { strict: true },
      },
      {
        path: '*',
        component: () =>
          import(
            /* webpackChunkName: "course" */ /* webpackPrefetch: true */ /* webpackPreload: true */ /* webpackMode: "lazy" */ '../views/externalCourse/externalCourse-about.vue'
          ),
        name: 'default-to-externalCourse-about',
      },
    ],
  },
  spaReloadRoute({
    path: '/search*',
    meta: {
      public: true,
    },
  }),
  spaReloadRoute({
    path: '/browse',
    name: 'browse',
    meta: {
      public: true,
    },
  }),
  spaReloadRoute({
    path: '/dashboard/teaching/created-by-me',
    name: 'my-teaching-resources',
    meta: {
      public: true,
    },
  }),
  {
    path: withLocale(`/professional-development*`),
    name: 'browse-courses-pd',
    alias: withLocale(`/browse`),
    redirect: {
      name: 'browse',
      query: { sort: 'Featured', r: 'courses' },
    },
  },
  {
    path: withLocale(`/community/member/:vanityURL`),
    name: 'member-profile',
    component: () =>
      import(
        /* webpackChunkName: "member" */ /* webpackPrefetch: true */ /* webpackPreload: true */ /* webpackMode: "lazy" */ '../views/member/member-profile.vue'
      ),
    meta: {
      public: true,
    },
  },
  {
    path: withLocale(`/community/discussion/new`),
    name: 'new-discussion',
    component: () =>
      import(
        /* webpackChunkName: "discussion" */ /* webpackPrefetch: true */ /* webpackPreload: true */ /* webpackMode: "lazy" */ '../views/discussion.vue'
      ),
  },
  {
    path: withLocale(`/community/discussion/:discussionID`),
    name: 'discussion',
    component: () =>
      import(
        /* webpackChunkName: "discussion" */ /* webpackPrefetch: true */ /* webpackPreload: true */ /* webpackMode: "lazy" */ '../views/discussion.vue'
      ),
    meta: {
      public: true,
    },
  },
  {
    path: withLocale(`/community*`),
    component: () =>
      import(
        /* webpackChunkName: "community" */ /* webpackPrefetch: true */ /* webpackPreload: true */ /* webpackMode: "lazy" */ '../views/community.vue'
      ),
    name: 'community',
    meta: {
      public: true,
    },
  },
  {
    path: withLocale(`/community`),
    component: () =>
      import(
        /* webpackChunkName: "community" */ /* webpackPrefetch: true */ /* webpackPreload: true */ /* webpackMode: "lazy" */ '../views/community.vue'
      ),
    name: 'community-non-wildcard',
    meta: {
      public: true,
    },
  },
  {
    path: withLocale(`/main/auth*`),
    name: 'auth',
    component: () =>
      import(
        /* webpackChunkName: "home" */ /* webpackPrefetch: true */ /* webpackPreload: true */ /* webpackMode: "lazy" */ '../views/auth.vue'
      ),
    meta: {
      public: true,
    },
  },
  {
    path: withLocale(`/account/settings`),
    name: 'member-settings',
    component: () =>
      import(
        /* webpackChunkName: "member" */ /* webpackPrefetch: true */ /* webpackPreload: true */ /* webpackMode: "lazy" */ '../views/member/member-settings.vue'
      ),
  },
  {
    path: withLocale(`/comment/:commentID`),
    name: 'comment',
    component: () =>
      import(
        /* webpackChunkName: "comment" */ /* webpackPrefetch: true */ /* webpackPreload: true */ /* webpackMode: "lazy" */ '../views/comment.vue'
      ),
    meta: {
      public: false,
    },
  },
  {
    path: withLocale(`/leaderboard`),
    name: 'leaderboard',
    component: () =>
      import(
        /* webpackChunkName: "leaderboard" */ /* webpackPrefetch: true */ /* webpackPreload: true */ /* webpackMode: "lazy" */ '../views/leaderboard.vue'
      ),
    meta: {
      public: true,
    },
  },
  {
    path: withLocale(`/help-center`),
    name: 'help-center',
    component: () =>
      import(
        /* webpackChunkName: "help-center" */ /* webpackPrefetch: true */ /* webpackPreload: true */ /* webpackMode: "lazy" */ '../views/help-center.vue'
      ),
    meta: {
      public: true,
    },
  },
  {
    path: withLocale(`/not-found*`),
    name: 'not-found',
    component: () =>
      import(
        /* webpackChunkName: "home" */ /* webpackPrefetch: true */ /* webpackPreload: true */ /* webpackMode: "lazy" */ '../views/not-found.vue'
      ),
    meta: {
      public: true,
    },
  },
  spaReloadRoute({
    path: '/',
    name: 'landing',
    meta: {
      public: true,
    },
  }),
  spaReloadRoute({
    path: '/teaching-resources',
    name: 'teaching-resources-non-wildcard',
    meta: {
      public: true,
    },
  }),
  spaReloadRoute({
    path: '/teaching-resources*',
    name: 'teaching-resources',
    meta: {
      public: true,
    },
  }),
  spaReloadRoute({
    path: '/professional-learning',
    name: 'professional-learning',
    meta: {
      public: true,
    },
  }),
  {
    path: withLocale(`/khan-create`),
    redirect: `${RedirectRoutePath.get('/khan-create')}`,
  },
  {
    path: withLocale('/aca/objectives/photoshop'),
    redirect: `${RedirectRoutePath.get('/aca/objectives/photoshop')}`,
  },
  {
    path: withLocale('/aca/objectives/illustrator'),
    redirect: `${RedirectRoutePath.get('/aca/objectives/illustrator')}`,
  },
  {
    path: withLocale('/aca/objectives/indesign'),
    redirect: `${RedirectRoutePath.get('/aca/objectives/indesign')}`,
  },
  {
    path: withLocale('/aca/objectives/premierepro'),
    redirect: `${RedirectRoutePath.get('/aca/objectives/premierepro')}`,
  },
  {
    path: withLocale('/aca/objectives/after-effects'),
    redirect: `${RedirectRoutePath.get('/aca/objectives/after-effects')}`,
  },
  {
    path: withLocale('/aca/objectives/animate'),
    redirect: `${RedirectRoutePath.get('/aca/objectives/animate')}`,
  },
  {
    path: withLocale('/aca/objectives/dreamweaver'),
    redirect: `${RedirectRoutePath.get('/aca/objectives/dreamweaver')}`,
  },
  {
    path: withLocale('/aca/objectives'),
    redirect: `${RedirectRoutePath.get('/aca/objectives')}`,
  },
  {
    path: withLocale('/aca/prep'),
    redirect: `${RedirectRoutePath.get('/aca/prep')}`,
  },
  {
    path: withLocale('/aca/whycertify'),
    redirect: `${RedirectRoutePath.get('/aca/whycertify')}`,
  },
  {
    path: withLocale('/aca/faq'),
    redirect: `${RedirectRoutePath.get('/aca/faq')}`,
  },
  {
    path: withLocale('/aca'),
    redirect: `${RedirectRoutePath.get('/aca')}`,
  },
  {
    path: withLocale('/aca/objectives'),
    redirect: '/adobe-certified-professional/exams',
  },
  {
    path: withLocale('/aca/prep'),
    redirect: '/adobe-certified-professional',
  },
  {
    path: withLocale('/aca/whycertify'),
    redirect: '/adobe-certified-professional',
  },
  {
    path: withLocale('/aca/faq'),
    redirect: '/adobe-certified-professional',
  },
  {
    path: withLocale('/aca'),
    redirect: '/adobe-certified-professional',
  },
  {
    path: withLocale('/spark'),
    redirect: `${RedirectRoutePath.get('/spark')}`,
  },
  // CCEDU-4534 Redirect WP pages
  {
    path: withLocale('/common-core'),
    redirect: '/',
  },
  {
    path: withLocale('/uk/adobe-for-teachers'),
    redirect: '/',
  },
  {
    path: withLocale('/parents'),
    redirect: '/',
  },
  {
    path: withLocale('/young-learners'),
    redirect: '/',
  },
  {
    path: withLocale('/distance-learning/digital-literacy'),
    redirect: '/',
  },
  {
    path: withLocale('/education-leader'),
    redirect: '/community',
  },
  spaReloadRoute({
    path: withLocale(`/:vanityURL(.+)`),
    name: 'static-page',
    meta: {
      public: true,
    },
  }),
  // {
  //   path: withLocale(`/:vanityURL(.+)`),
  //   name: 'static-page',
  //   component: () =>
  //     import(
  //       /* webpackChunkName: "static-page" */ /* webpackPrefetch: true */ /* webpackPreload: true */ /* webpackMode: "lazy" */ '../views/static-page.vue'
  //     ),
  //   meta: {
  //     public: true,
  //   },
  // },
  {
    path: '*',
    name: 'global-not-found',
    component: () =>
      import(
        /* webpackChunkName: "home" */ /* webpackPrefetch: true */ /* webpackPreload: true */ /* webpackMode: "lazy" */ '../views/not-found.vue'
      ),
    meta: {
      public: true,
    },
  },
];
